import { useEffect } from 'react'

/**
 * 单页应用没有服务端渲染，`index.html` 里的 title/description/og/canonical
 * 永远是首页的兜底值（见 index.html 头部注释）。这个 hook 让路由级页面在客户端
 * 接管这几项，卸载时**必须**还原回默认值 —— 否则从作品详情页跳到没接这个 hook
 * 的页面（登录、管理后台……）时，标签页标题会一直停在上一页。
 */

export const SITE_NAME = 'APKGal'
export const SITE_URL = 'https://apkgal.com'
export const DEFAULT_TITLE = 'APKGal - 安卓 galgame 移植分享站'
export const DEFAULT_DESCRIPTION =
  "APKGal (apkgal.com) 专注分享移植到 Android 的 galgame，提供视觉小说安卓移植版、吉里吉里 / ONScripter / Ren'Py 等引擎的移植包、汉化补丁与安装教程，方便玩家在手机上找到能玩的 galgame"

export interface PageMetaOptions {
  /** 完整标题，调用方自己拼站名后缀（如 `${postTitle} - APKGal`），首页可直接传 DEFAULT_TITLE。 */
  title: string
  description?: string
  /** 站内相对路径，如 `/mods/foo`。省略时用当前 `location.pathname`。 */
  path?: string
  /** 搜索结果页等不该被收录的页面；`/search` 已在 robots.txt 里 Disallow，这里是双重保险。 */
  noindex?: boolean
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

function setRobots(content: string | null) {
  const el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (content === null) {
    el?.remove()
    return
  }
  if (el) {
    el.setAttribute('content', content)
    return
  }
  const created = document.createElement('meta')
  created.setAttribute('name', 'robots')
  created.setAttribute('content', content)
  document.head.appendChild(created)
}

function apply(title: string, description: string, url: string, noindex: boolean) {
  document.title = title
  setMeta('name', 'description', description)
  setCanonical(url)
  setMeta('property', 'og:title', title)
  setMeta('property', 'og:description', description)
  setMeta('property', 'og:url', url)
  setMeta('name', 'twitter:title', title)
  setMeta('name', 'twitter:description', description)
  setRobots(noindex ? 'noindex, nofollow' : null)
}

export function usePageMeta({ title, description, path, noindex = false }: PageMetaOptions) {
  useEffect(() => {
    const url = `${SITE_URL}${path ?? window.location.pathname}`
    apply(title, description ?? DEFAULT_DESCRIPTION, url, noindex)

    return () => {
      apply(DEFAULT_TITLE, DEFAULT_DESCRIPTION, `${SITE_URL}/`, false)
    }
  }, [title, description, path, noindex])
}
